<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {router, useForm} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import VFieldLabel from "@/components/forms/VFieldLabel.vue";
import {useAuth} from "@/composables/auth";
import VEditor from "@/components/forms/VEditor.vue";
import {onMounted, ref, watch} from "vue";
import {CapacityFrameworkSummary, TeamSummary, UserSummary} from "@/types";
import axios from "axios";
import {useDate} from "@/composables/date";
import {useTeam} from "@/composables/team";

defineOptions({layout: BaseLayout})

const {teamsWithPermissions} = useTeam()
const { user } = useAuth()
const {shortDate} = useDate()

interface Props {
    title: string
}

const props = defineProps<Props>();

const form = useForm({
    capacity_framework_id: null as number | null,
    capacity_framework_version_id: null as number | null,
    title: '',
    description: '',
    user: user.value,
    team_id: null as number | null,
    is_public: true,
    start_date: new Date().toISOString().split('T')[0],
    due_date: null as string | null,
})

const isBeingEdited = ref<boolean>(false)
const isDateBeingEdited = ref<boolean>(false)
const search = ref<string>('')
const capacityFrameworkSearch = ref<string>('')
const items = ref<Array<UserSummary>>([])
const capacityFrameworkItems = ref<Array<CapacityFrameworkSummary>>([])
const versionItems = ref<Array<{label: string, value: number}>>([])
const selectedTeam = ref<TeamSummary | null>(null);
const selectedCapacityFramework = ref<CapacityFrameworkSummary | null>(null);

const submit = () => {
    form.post(route('assessments.store'))
}

const goBack = () => {
    router.get(route('assessments.index'))
}

const toggleEdit = (value?: UserSummary | null) => {
    if (!value) return
    isBeingEdited.value = !isBeingEdited.value;
    if (isBeingEdited.value) {
        search.value = form.user.name || '';
    } else {
        search.value = '';
    }
}

const toggleDateEdit = () => {
    isDateBeingEdited.value = !isDateBeingEdited.value;
}

const closeEdit = () => {
    isBeingEdited.value = false;
    search.value = '';
}

const closeDateEdit = () => {
    isDateBeingEdited.value = false;
}

const loadCapacityFrameworks = async (search: string) => {
    try {
        const { data } = await axios.get(route('capacity-frameworks.search'), {
            params: { search }
        });

        capacityFrameworkItems.value = data;
    } catch (error) {
        capacityFrameworkItems.value = [];
    }
};

const loadVersions = async (capacityFrameworkId: number) => {
    try {
        const { data } = await axios.get(route('capacity-frameworks.versions', {id: capacityFrameworkId}));

        versionItems.value = data;
    } catch (error) {
        versionItems.value = [];
    }
};

const loadItems = async (search: string) => {
    if (!selectedTeam.value) {
        items.value = [];
        return;
    }

    try {
        const { data } = await axios.get(route('teams.members', { team: selectedTeam.value.id }), {
            params: { search }
        });

        items.value = data;
    } catch (error) {
        items.value = [];
    }
};

const assignYourself = () => {
    form.user = user.value;
    isBeingEdited.value = false;
}

const selectTeam = (team: TeamSummary) => {
    form.team_id = team.id;
    selectedCapacityFramework.value = null;
    form.capacity_framework_id = null;
    form.capacity_framework_version_id = null;
}

const selectCapacityFramework = (framework: CapacityFrameworkSummary) => {
    form.capacity_framework_id = framework.id;
    form.capacity_framework_version_id = framework.version_id;
    form.title = `${framework.name} assessment ${new Date().getFullYear()}`;
}

watch(search, (newSearch) => {
    loadItems(newSearch);
}, {immediate: true})

watch(capacityFrameworkSearch, (newSearch) => {
    loadCapacityFrameworks(newSearch);
}, {immediate: true})

watch(selectedCapacityFramework, (newCapacityFramework) => {
    if (newCapacityFramework) {
        loadVersions(newCapacityFramework.id);
    }
}, {immediate: true})
</script>

<template>
    <v-container>
        <v-row class="align-center justify-space-between px-3">
            <h1 class="text-h4 font-weight-bold">
                {{ props.title }}
            </h1>
        </v-row>
        <v-form
            @submit.prevent="submit"
        >
            <v-row
                class="mt-5"
            >
                <v-col
                    cols="12"
                    md="6"
                    class="py-0"
                >
                    <v-field-label>
                        Resource (required)
                    </v-field-label>
                    <v-select
                        v-model="selectedTeam"
                        :items="teamsWithPermissions"
                        return-object
                        hide-selected
                        hide-details
                        hide-no-data
                        item-title="name"
                        item-value="id"
                        density="compact"
                        :list-props="{
                            class: 'py-0'
                        }"
                        :menu-props="{
                            location: 'bottom'
                        }"
                        @update:model-value="selectTeam($event)"
                    />
                </v-col>
            </v-row>
            <v-row
                v-if="selectedTeam"
                class="mt-5"
            >
                <v-col
                    cols="12"
                    md="6"
                    class="py-0"
                >
                    <v-field-label>
                        Framework (required)
                    </v-field-label>
                    <v-autocomplete
                        v-model="selectedCapacityFramework"
                        v-model:search="capacityFrameworkSearch"
                        variant="outlined"
                        :items="capacityFrameworkItems"
                        density="compact"
                        return-object
                        hide-details
                        hide-no-data
                        no-filter
                        item-title="name"
                        item-value="id"
                        hide-selected
                        :list-props="{
                            class: 'py-0'
                        }"
                        :menu-props="{
                            location: 'bottom'
                        }"
                        @update:model-value="selectCapacityFramework($event)"
                    >
                        <template #item="{ item, props: binds }">
                            <v-list-item
                                :value="binds.value"
                                link
                                @click="binds.onClick as Event"
                            >
                                <v-list-item-title
                                    class="text-body-2 font-weight-bold"
                                >
                                    {{ item.raw.name }}
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-autocomplete>
                </v-col>
            </v-row>
            <v-row
                v-if="selectedCapacityFramework"
                class="mt-5"
            >
                <v-col
                    cols="12"
                    md="6"
                    class="py-0"
                >
                    <v-field-label>
                        Version (required)
                    </v-field-label>
                    <v-select
                        v-model="form.capacity_framework_version_id"
                        variant="outlined"
                        :items="versionItems"
                        density="compact"
                        hide-details
                        hide-no-data
                        no-filter
                        item-title="label"
                        item-value="value"
                        hide-selected
                        :list-props="{
                            class: 'py-0'
                        }"
                        :menu-props="{
                            location: 'bottom'
                        }"
                    >
                        <template #item="{ item, props: binds }">
                            <v-list-item
                                :value="binds.value"
                                link
                                @click="binds.onClick as Event"
                            >
                                <v-list-item-title
                                    class="text-body-2 font-weight-bold"
                                >
                                    {{ item.raw.label }}
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-select>
                </v-col>
            </v-row>
            <v-row
                v-if="selectedTeam && selectedCapacityFramework"
                class="mt-5"
            >
                <v-col
                    cols="12"
                    class="py-0"
                >
                    <v-field-label>
                        Title (required)
                    </v-field-label>
                    <v-text-field
                        v-model="form.title"
                        type="text"
                        name="title"
                        class="mb-3"
                        density="compact"
                        :hide-details="!form.errors.title"
                        :error-messages="form.errors.title"
                        autofocus
                    />
                </v-col>
                <v-col
                    cols="12"
                    md="8"
                    class="py-0"
                >
                    <v-field-label>
                        Description
                    </v-field-label>
                    <v-editor
                        v-model="form.description"
                        name="description"
                        placeholder="Write a comment"
                    />
                    <v-checkbox
                        v-model="form.is_public"
                        class="mt-3 text-body-2"
                        density="compact"
                        :true-value="false"
                        :false-value="true"
                        color="primary"
                        hide-details
                    >
                        <template #label>
                            <span class="text-body-2">
                                Turn on confidentiality: Limit visibility of answers to resource members only
                            </span>
                        </template>
                    </v-checkbox>
                </v-col>
                <v-col
                    cols="12"
                    md="4"
                >
                    <v-list
                        class="pa-0"
                    >
                        <v-list-item v-click-outside="{ handler: closeEdit }">
                            <v-list-item-title class="text-body-2 text-black font-weight-bold">
                                Main contact
                                <v-btn
                                    color="black"
                                    variant="text"
                                    size="x-small"
                                    class="float-end py-1 px-2"
                                    @click="toggleEdit"
                                >
                                    <span class="font-weight-regular">
                                        {{ isBeingEdited ? 'Apply' : 'Edit' }}
                                    </span>
                                </v-btn>
                            </v-list-item-title>
                            <v-list-item-subtitle
                                v-if="!isBeingEdited"
                                class="d-inline-flex"
                            >
                                {{ form.user?.name || 'None' }} <div
                                    v-if="!form.user"
                                    class="ml-1"
                                >
                                    - <span
                                        class="custom-link"
                                        @click="assignYourself"
                                    >assign yourself</span>
                                </div>
                            </v-list-item-subtitle>
                            <v-autocomplete
                                v-if="isBeingEdited"
                                v-model="form.user"
                                v-model:search="search"
                                variant="outlined"
                                :items="items"
                                density="compact"
                                return-object
                                hide-details
                                hide-no-data
                                no-filter
                                item-title="name"
                                item-value="id"
                                hide-selected
                                :list-props="{
                                    class: 'py-0'
                                }"
                                :menu-props="{
                                    location: 'bottom'
                                }"
                                menu-icon="null"
                                @update:model-value="toggleEdit($event)"
                            >
                                <template #item="{ item, props: binds }">
                                    <v-list-item
                                        :value="binds.value"
                                        link
                                        @click="binds.onClick as Event"
                                    >
                                        <v-list-item-title
                                            class="text-body-2 font-weight-bold"
                                        >
                                            {{ item.raw.name }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle
                                            class="text-body-2 text-medium-emphasis"
                                        >
                                            {{ item.raw.email }}
                                        </v-list-item-subtitle>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                            <small
                                v-if="form.errors.user"
                                class="d-block text-error"
                            >
                                {{ form.errors.user }}
                            </small>
                            <v-divider class="mt-3 mb-1" />
                        </v-list-item>
                        <v-list-item v-click-outside="{ handler: closeDateEdit }">
                            <v-list-item-title class="text-body-2 text-black font-weight-bold">
                                Dates
                                <v-btn
                                    color="black"
                                    variant="text"
                                    size="x-small"
                                    class="float-end py-1 px-2"
                                    @click="toggleDateEdit"
                                >
                                    <span class="font-weight-regular">
                                        {{ isDateBeingEdited ? 'Apply' : 'Edit' }}
                                    </span>
                                </v-btn>
                            </v-list-item-title>
                            <div
                                v-if="!isDateBeingEdited"
                                class="mb-2 text-body-2"
                            >
                                Start: <span class="ml-1 text-medium-emphasis">{{ form.start_date ? shortDate(form.start_date) : 'None' }}</span>
                            </div>
                            <v-list-item-subtitle
                                v-if="isDateBeingEdited"
                            >
                                <v-text-field
                                    v-model="form.start_date"
                                    type="date"
                                    density="compact"
                                    max-width="80%"
                                    hide-details
                                    clearable
                                >
                                    <template #prepend>
                                        <span class="text-body-2">Start:</span>
                                    </template>
                                </v-text-field>
                            </v-list-item-subtitle>
                            <div
                                v-if="!isDateBeingEdited"
                                class="mb-2 text-body-2"
                            >
                                Due: <span class="ml-3 text-medium-emphasis">{{ form.due_date ? shortDate(form.due_date) : 'None' }}</span>
                            </div>
                            <v-list-item-subtitle
                                v-if="isDateBeingEdited"
                            >
                                <v-text-field
                                    v-model="form.due_date"
                                    type="date"
                                    density="compact"
                                    max-width="80%"
                                    hide-details
                                >
                                    <template #prepend>
                                        <span class="text-body-2">Due:</span>
                                    </template>
                                </v-text-field>
                            </v-list-item-subtitle>
                        </v-list-item>
                    </v-list>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    cols="12"
                >
                    <v-btn
                        :disabled="!selectedTeam || !selectedCapacityFramework"
                        color="primary"
                        type="submit"
                    >
                        Create assessment
                    </v-btn>
                    <v-btn
                        color="black"
                        variant="text"
                        border="sm"
                        class="ml-2"
                        @click="goBack"
                    >
                        <span class="font-weight-regular">
                            Cancel
                        </span>
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<style scoped>
.text-sm {
    font-size: 0.825rem !important;
}
</style>
